import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Clock } from "lucide-react";
import { LuxuryBackground, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { ARTICLES } from "../data/insights-articles";
import InsightsSubscribe from "../components/InsightsSubscribe";
import { usePageNavigate } from "../hooks/usePageNavigate";

function ArticleDetail({ article, onBack }) {
  const { navigateTo } = usePageNavigate();
  return (
    <div className="relative" data-testid="article-detail">
      <div className="relative overflow-hidden h-[340px] md:h-[420px]">
        <img src={article.heroImage} alt={article.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/70 to-emerald-950/30" />
        <div className="absolute inset-0 z-10 flex flex-col justify-end mx-auto max-w-4xl px-4 pb-10 sm:px-6 lg:px-8">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors mb-6 w-fit" data-testid="article-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </button>
          <div className="flex items-center gap-3 mb-4">
            <span className="rounded-full bg-gold/20 border border-gold/40 px-3 py-1 text-xs font-semibold text-gold uppercase tracking-wider">{article.category}</span>
            <span className="flex items-center gap-1.5 text-xs text-cream/60"><Clock className="h-3 w-3" />{article.readTime}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-cream leading-tight">{article.title}</h1>
          <p className="mt-4 text-base md:text-lg text-cream/80 max-w-3xl leading-relaxed">{article.subtitle}</p>
        </div>
      </div>

      <div className="relative">
        <LuxuryBackground />
        <article className="relative z-10 mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
          {article.sections.map((section, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }}
              className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-5 tracking-tight">{section.heading}</h2>
              <div className="prose prose-lg max-w-none">
                {section.content.split("\n\n").map((paragraph, j) => {
                  if (paragraph.startsWith("- ")) {
                    const items = paragraph.split("\n").filter(l => l.startsWith("- "));
                    return (
                      <ul key={j} className="space-y-2.5 my-4">
                        {items.map((item, k) => (
                          <li key={k} className="flex items-start gap-3 text-base leading-7 text-[#444]">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                            <span>{item.replace(/^- /, "")}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (/^\d+\./.test(paragraph.trim())) {
                    const items = paragraph.split("\n").filter(l => /^\d+\./.test(l.trim()));
                    return (
                      <ol key={j} className="space-y-3 my-4 list-none">
                        {items.map((item, k) => (
                          <li key={k} className="flex items-start gap-3 text-base leading-7 text-[#444]">
                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">{k + 1}</span>
                            <span>{item.replace(/^\d+\.\s*/, "").replace(/\s*—\s*/, " — ")}</span>
                          </li>
                        ))}
                      </ol>
                    );
                  }
                  return <p key={j} className="text-base leading-7 text-[#444] mb-4">{paragraph}</p>;
                })}
              </div>
            </motion.div>
          ))}

          <div className="mt-12">
            <InsightsSubscribe />
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-16 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 text-center">
            <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">Ready to Put This Knowledge to Work?</h3>
            <p className="text-base text-[#555] max-w-xl mx-auto mb-6">
              Have a commercial property to sell or capital to deploy? Let's talk.
            </p>
            <GoldButton onClick={() => navigateTo("sell")} data-testid="article-cta-submit">
              Submit Your Property <ArrowRight className="h-4 w-4 ml-1" />
            </GoldButton>
          </motion.div>
        </article>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const { navigateTo } = usePageNavigate();
  const [activeArticle, setActiveArticle] = useState(null);

  if (activeArticle) {
    return <ArticleDetail article={activeArticle} onBack={() => setActiveArticle(null)} />;
  }

  return (
    <div className="relative" data-testid="insights-page">
      <PageHero eyebrow="Insights" title="Market Insights"
        description="Explore commercial real estate market analysis, acquisition strategy, and investment insights from our team." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2">
            {ARTICLES.map((article, i) => (
              <motion.div key={article.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="h-full cursor-pointer group" onClick={() => setActiveArticle(article)} data-testid={`insight-card-${article.slug}`}>
                  <Card className="h-full flex flex-col overflow-hidden !p-0">
                    <div className="relative h-48 overflow-hidden">
                      <img src={article.heroImage} alt={article.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-4 flex items-center gap-2">
                        <span className="rounded-full bg-gold/20 border border-gold/40 backdrop-blur-sm px-2.5 py-0.5 text-[10px] font-semibold text-gold uppercase tracking-wider">{article.category}</span>
                        <span className="flex items-center gap-1 text-[10px] text-white/70"><Clock className="h-3 w-3" />{article.readTime}</span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-lg font-bold text-[#0A0A0A] mb-2 group-hover:text-emerald-700 transition-colors leading-snug">{article.title}</h3>
                      <p className="text-sm text-[#555] leading-relaxed flex-1">{article.subtitle}</p>
                      <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-700 group-hover:text-emerald-600 transition-colors">
                        Read Article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-12">
            <InsightsSubscribe />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-16 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 text-center">
            <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">Ready to Put This Knowledge to Work?</h3>
            <p className="text-base text-[#555] max-w-xl mx-auto mb-6">
              Whether you're selling a property or looking to invest, our team is ready to help.
            </p>
            <GoldButton onClick={() => navigateTo("sell")} data-testid="insights-cta-analyze">
              Submit a Property <ArrowRight className="h-4 w-4 ml-1" />
            </GoldButton>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
